import _get from 'lodash.get';
import { isContentfulString, isEmptyObject } from '@philips-internal/oswald-ui-utils';
import { ConfigurationService } from '../configuration-service/index.js';
import { HistoryService } from '../history-service/index.js';
import { verifyResponseStatus } from '../http-service/http-service-utils.js';
import { HttpHeader, HttpService, HttpStatus } from '../http-service/index.js';
import { MultiFactorAuthenticationRequiredError } from './authentication-errors.js';
import { ACCESS_TOKEN, CLIENT_ID, GRANT_TYPE, Logical, MULTI_FACTOR_ROLE, MULTI_FACTOR_TOKEN_TYPE, OAuthGrantType, REFRESH_TOKEN, TOKEN_TYPE } from './authentication-service-constants.js';
import { decryptToken } from './decrypt-token.js';
import { JWTToken } from './JWTToken.js';
import { createMultiFactorAuthentication } from './multi-factor-authentication.js';
import { PermissionMatcher } from './permission-matcher.js';
import { requestRefreshToken as requestToken, revokeRefreshToken } from './request-refresh-token.js';
function getConfiguration() {
    const configuration = ConfigurationService.getAuthenticationConfiguration();
    if (!configuration || isEmptyObject(configuration)) {
        throw authenticationError('Service is not configured.');
    }
    return configuration;
}
function retrieveStorageData() {
    return {
        accessToken: sessionStorage.getItem(ACCESS_TOKEN) || '',
        refreshToken: sessionStorage.getItem(REFRESH_TOKEN) || '',
        tokenType: sessionStorage.getItem(TOKEN_TYPE) || '',
    };
}
function authenticationError(message) {
    return new Error(`[Authentication Service]: ${message}`);
}
function createAuthenticationService() {
    const logoutListeners = new Set();
    let jwtToken = null;
    let matcher = null;
    let pendingRefresh = null;
    const createToken = (type, accessToken) => {
        const { clientId, encryptedToken, endpoints } = getConfiguration();
        if (!encryptedToken) {
            return Promise.resolve(new JWTToken(type, accessToken, accessToken));
        }
        const headers = createHeaders();
        const parameters = { accessToken };
        const options = {
            clientId,
            endpoint: endpoints.decryptToken,
            headers,
            parameters,
        };
        return decryptToken(options)
            .then(response => new JWTToken(type, response.token, accessToken));
    };
    const createTokenFromResponse = (response) => {
        return createToken(response.token_type, response.access_token);
    };
    const restoreTokenFromStorage = () => {
        const { accessToken, tokenType } = retrieveStorageData();
        if (isContentfulString(accessToken) && isContentfulString(tokenType)) {
            return createToken(tokenType, accessToken).then(token => {
                jwtToken = token;
                matcher = new PermissionMatcher(token);
                return token;
            });
        }
        return Promise.resolve(null);
    };
    const clearTokens = () => {
        jwtToken = null;
        matcher = null;
        sessionStorage.removeItem(ACCESS_TOKEN);
        sessionStorage.removeItem(REFRESH_TOKEN);
        sessionStorage.removeItem(TOKEN_TYPE);
        logoutListeners.forEach((listener) => listener());
    };
    const checkMultiFactorAuthentication = (token) => {
        if (token.getType() === MULTI_FACTOR_TOKEN_TYPE || (Array.isArray(token.roles) && token.roles.includes(MULTI_FACTOR_ROLE))) {
            throw new MultiFactorAuthenticationRequiredError(token);
        }
    };
    const verifyToken = () => {
        if (!getJWTToken()) {
            throw authenticationError('Missing JWToken for Multi Factor Authentication.');
        }
    };
    const refreshToken = (parameters) => {
        if (pendingRefresh !== null) {
            return pendingRefresh;
        }
        const { clientId, endpoints } = getConfiguration();
        const headers = createHeaders();
        const options = {
            clientId,
            endpoint: endpoints.refresh,
            headers,
            parameters,
        };
        const refreshPromise = requestToken(options)
            .then((response) => {
            pendingRefresh = null;
            return verifyResponse(response);
        })
            .then((response) => response.json())
            .then((response) => {
            if (verifyCanStoreToken(parameters)) {
                return storeToken(response);
            }
            throw authenticationError('User logged out while the token was being refreshed');
        })
            .catch((e) => {
            pendingRefresh = null;
            throw e;
        });
        pendingRefresh = refreshPromise;
        return refreshPromise;
    };
    const verifyCanStoreToken = (parameters) => {
        const { accessToken, refreshToken } = retrieveStorageData();
        if (parameters[GRANT_TYPE] !== REFRESH_TOKEN || (accessToken && refreshToken)) {
            return true;
        }
        clearTokens();
        return false;
    };
    const revokeToken = () => {
        const { clientId, endpoints } = getConfiguration();
        const { refreshToken } = retrieveStorageData();
        const parameters = {
            [REFRESH_TOKEN]: refreshToken,
        };
        const headers = createHeaders();
        const options = {
            clientId,
            endpoint: endpoints.revoke,
            headers,
            parameters,
        };
        return revokeRefreshToken(options);
    };
    const storeToken = async (response) => {
        return createTokenFromResponse(response).then(token => {
            checkMultiFactorAuthentication(token);
            jwtToken = token;
            matcher = new PermissionMatcher(token);
            sessionStorage.setItem(ACCESS_TOKEN, response.access_token);
            sessionStorage.setItem(REFRESH_TOKEN, response.refresh_token);
            sessionStorage.setItem(TOKEN_TYPE, response.token_type);
            return token;
        });
    };
    const verifyResponse = (response) => {
        if (response.status === HttpStatus.UNAUTHORIZED) {
            clearTokens();
            redirectToLogin(true);
        }
        return verifyResponseStatus(response);
    };
    function getErrorMessageFromResponse(response) {
        const { errorResponseMapper } = getConfiguration();
        if (!errorResponseMapper) {
            throw authenticationError('Error responses are not configured.');
        }
        const { responseCodeMessageMap, responseCodePath } = errorResponseMapper;
        return response.clone().json().then((response) => {
            const code = String(_get(response, responseCodePath));
            return responseCodeMessageMap[code];
        });
    }
    function getHeaders() {
        const token = getJWTToken();
        const headers = [];
        if (token) {
            headers.push([HttpHeader.AUTHORIZATION, `${token.getType()} ${token.rawToken()}`]);
        }
        return headers;
    }
    function getJWTToken() {
        return jwtToken;
    }
    function getRefreshToken() {
        const { refreshToken } = retrieveStorageData();
        return refreshToken;
    }
    function isAuthenticated() {
        return jwtToken !== null;
    }
    function listenLogout(listener) {
        if (logoutListeners.has(listener)) {
            throw authenticationError('Listener already added.');
        }
        logoutListeners.add(listener);
        return () => logoutListeners.delete(listener);
    }
    function hasLogoutListeners() {
        return logoutListeners.size > 0;
    }
    function login(parameters) {
        if (isAuthenticated()) {
            throw authenticationError('Already authenticated.');
        }
        return refreshToken({
            [GRANT_TYPE]: OAuthGrantType.PASSWORD,
            ...parameters,
        });
    }
    function loginWithAuthorizationCode(code) {
        if (isAuthenticated()) {
            throw authenticationError('Already authenticated.');
        }
        return refreshToken({
            code,
            [GRANT_TYPE]: OAuthGrantType.AUTHORIZATION_CODE,
        });
    }
    function loginWithResponse(response) {
        return storeToken(response);
    }
    function createHeaders() {
        const headers = new Headers();
        const token = getJWTToken();
        if (token) {
            headers.append(HttpHeader.AUTHORIZATION, `${token.getType()} ${token.rawToken()}`);
        }
        return headers;
    }
    function logout() {
        try {
            return revokeToken();
        }
        finally {
            clearTokens();
        }
    }
    function matchClaims(claims, logical = Logical.AND) {
        if (!isAuthenticated()) {
            return false;
        }
        return matcher ? matcher.matchClaims(claims, logical) : false;
    }
    function matchPermissions(permissions, logical = Logical.AND) {
        if (!isAuthenticated()) {
            return false;
        }
        return matcher ? matcher.matchPermissions(permissions, logical) : false;
    }
    function redirectToForgotPassword() {
        const configuration = getConfiguration();
        HistoryService.push(configuration.routes.forgotPassword);
    }
    function redirectToHome() {
        const configuration = getConfiguration();
        HistoryService.push(configuration.routes.home);
    }
    function redirectToInactivity() {
        const configuration = getConfiguration();
        if (isContentfulString(configuration.openIDConnect?.externalLogoutURL)) {
            return void redirectToOIDCLogout(configuration.routes.inactivity);
        }
        HistoryService.push(configuration.routes.inactivity);
    }
    function redirectToLogin(keepRouteState = false) {
        const configuration = getConfiguration();
        if (isContentfulString(configuration.openIDConnect?.externalLogoutURL)) {
            return void redirectToOIDCLogout(configuration.routes.login);
        }
        const { state } = HistoryService.getCurrentRouteQueryParams();
        const options = state && keepRouteState ? { searchParams: { state } } : {};
        HistoryService.push(configuration.routes.login, {}, options);
    }
    async function redirectToOIDCLogout(fallbackRoute) {
        const configuration = getConfiguration();
        const redirectRoute = isContentfulString(fallbackRoute) ? fallbackRoute : configuration.routes.login;
        if (!configuration.openIDConnect || !isContentfulString(configuration.openIDConnect?.externalLogoutURL)) {
            return HistoryService.push(redirectRoute);
        }
        try {
            const connector = configuration.openIDConnect.externalLogoutURL.includes('?') ? '&' : '?';
            const response = await HttpService.get(`${configuration.openIDConnect.externalLogoutURL}${connector}redirect=${fallbackRoute}`);
            const url = response.headers.get('Location');
            if (!isContentfulString(url)) {
                return HistoryService.push(redirectRoute);
            }
            const isInternalRouteURL = url.startsWith(window.location.origin);
            if (isInternalRouteURL) {
                return HistoryService.push(url);
            }
            HistoryService.redirect(url);
        }
        catch (error) {
            if (!isContentfulString(configuration.openIDConnect.errorRoute)) {
                return HistoryService.push(redirectRoute);
            }
            HistoryService.push(configuration.openIDConnect.errorRoute, {}, { state: { error } });
        }
    }
    function requestRefreshToken(parameters = {}) {
        const params = {
            [GRANT_TYPE]: REFRESH_TOKEN,
            [REFRESH_TOKEN]: retrieveStorageData().refreshToken,
            ...parameters,
        };
        return refreshToken(params);
    }
    function updateJWTToken(response) {
        return storeToken(response);
    }
    function validateCredentials(parameters) {
        const { clientId, endpoints } = getConfiguration();
        const headers = createHeaders();
        const requestParamters = {
            [CLIENT_ID]: clientId,
            [GRANT_TYPE]: OAuthGrantType.PASSWORD,
            ...parameters,
        };
        const refreshTokenOptions = {
            clientId,
            endpoint: endpoints.refresh,
            headers,
            parameters: requestParamters,
        };
        return requestToken(refreshTokenOptions)
            .then(response => verifyResponseStatus(response))
            .then(response => response.json())
            .then(async (response) => {
            const token = await createTokenFromResponse(response);
            checkMultiFactorAuthentication(token);
            return Promise.resolve({ response, token });
        });
    }
    const multiFactorAuthentication = createMultiFactorAuthentication({
        createHeaders,
        createTokenFromResponse,
        getConfiguration,
        storeToken,
        verifyResponse,
        verifyResponseStatus,
        verifyToken,
    });
    return {
        ...multiFactorAuthentication,
        getErrorMessageFromResponse,
        getHeaders,
        getJWTToken,
        getRefreshToken,
        hasLogoutListeners,
        isAuthenticated,
        listenLogout,
        login,
        loginWithAuthorizationCode,
        loginWithResponse,
        logout,
        matchClaims,
        matchPermissions,
        redirectToForgotPassword,
        redirectToHome,
        redirectToInactivity,
        redirectToLogin,
        redirectToOIDCLogout,
        requestRefreshToken,
        restoreTokenFromStorage,
        updateJWTToken,
        validateCredentials,
    };
}
const AuthenticationService = createAuthenticationService();
export { AuthenticationService, };
//# sourceMappingURL=authentication-service.js.map